import React, { useState, useEffect } from "react";
import { PaiementService } from "../../services/paiements/paiementService";
import { Building2, Search, Plus, Edit2, Trash2, Check, X } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "react-hot-toast";

interface CompteBancaire {
  id: string;
  nom: string;
  banque: string;
  iban: string;
  bic: string;
  email_contact: string;
  est_actif: boolean;
  created_at: string;
}

export const AdminBanques: React.FC = () => {
  const [comptes, setComptes] = useState<CompteBancaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCompte, setCurrentCompte] = useState<Partial<CompteBancaire> | null>(null);

  const fetchComptes = async () => {
    try {
      setLoading(true);
      const response = await PaiementService.getComptesBancaires();
      setComptes(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement comptes bancaires", error);
      toast.error("Impossible de charger les comptes bancaires");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComptes();
  }, []);

  const filteredComptes = comptes.filter(
    (c) =>
      c.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.banque.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.iban.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenModal = (compte?: CompteBancaire) => {
    if (compte) {
      setCurrentCompte({ ...compte });
    } else {
      setCurrentCompte({ nom: "", banque: "", iban: "", bic: "", email_contact: "", est_actif: true });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentCompte(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompte) return;

    try {
      setLoading(true);
      if (currentCompte.id) {
        await PaiementService.updateCompteBancaire(currentCompte.id, currentCompte);
        toast.success("Compte bancaire mis à jour");
      } else {
        await PaiementService.createCompteBancaire(currentCompte);
        toast.success("Compte bancaire créé");
      }
      handleCloseModal();
      fetchComptes();
    } catch (error) {
      console.error("Erreur sauvegarde compte bancaire", error);
      toast.error("Erreur lors de la sauvegarde du compte bancaire");
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, nom: string) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer le compte "${nom}" ?`)) return;

    try {
      setLoading(true);
      await PaiementService.deleteCompteBancaire(id);
      toast.success("Compte bancaire supprimé");
      fetchComptes();
    } catch (error) {
      console.error("Erreur suppression compte bancaire", error);
      toast.error("Erreur lors de la suppression du compte bancaire");
      setLoading(false);
    }
  };

  const toggleStatus = async (compte: CompteBancaire) => {
    try {
      await PaiementService.updateCompteBancaire(compte.id, { ...compte, est_actif: !compte.est_actif });
      toast.success(`Compte ${!compte.est_actif ? "activé" : "désactivé"}`);
      fetchComptes();
    } catch (error) {
      toast.error("Erreur lors de la modification du statut");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comptes Bancaires Afflux</h1>
          <p className="mt-1 text-sm text-gray-600">Gérez les comptes émetteurs pour les virements fournisseur</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Compte
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
              placeholder="Rechercher une banque, IBAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading && comptes.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom / Banque
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RIB
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Contact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredComptes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                      Aucun compte bancaire trouvé.
                    </td>
                  </tr>
                ) : (
                  filteredComptes.map((compte) => (
                    <tr key={compte.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-primary-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{compte.nom}</div>
                            <div className="text-sm text-gray-500">{compte.banque}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{compte.iban}</div>
                        <div className="text-xs text-gray-500 font-mono">BIC: {compte.bic || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {compte.email_contact}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleStatus(compte)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                            compte.est_actif ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}
                        >
                          {compte.est_actif ? <Check className="w-3 h-3 mr-1" /> : <X className="w-3 h-3 mr-1" />}
                          {compte.est_actif ? "Actif" : "Inactif"}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleOpenModal(compte)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(compte.id, compte.nom)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseModal}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    {currentCompte?.id ? "Modifier le compte bancaire" : "Nouveau compte bancaire"}
                  </h3>
                  <div className="mt-4">
                    <form onSubmit={handleSave} className="space-y-4">
                      <div>
                        <label htmlFor="nom" className="block text-sm font-medium text-gray-700">Nom du compte</label>
                        <input
                          type="text"
                          id="nom"
                          required
                          value={currentCompte?.nom || ""}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, nom: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="Mtn CI Principal"
                        />
                      </div>
                      <div>
                        <label htmlFor="banque" className="block text-sm font-medium text-gray-700">Nom de la banque</label>
                        <input
                          type="text"
                          id="banque"
                          required
                          value={currentCompte?.banque || ""}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, banque: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="MTN Mobile Money"
                        />
                      </div>
                      <div>
                        <label htmlFor="iban" className="block text-sm font-medium text-gray-700">IBAN / Numéro de compte</label>
                        <input
                          type="text"
                          id="iban"
                          required
                          value={currentCompte?.iban || ""}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, iban: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="bic" className="block text-sm font-medium text-gray-700">BIC (Optionnel)</label>
                        <input
                          type="text"
                          id="bic"
                          value={currentCompte?.bic || ""}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, bic: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="email_contact" className="block text-sm font-medium text-gray-700">Email Contact Banque</label>
                        <input
                          type="email"
                          id="email_contact"
                          required
                          value={currentCompte?.email_contact || ""}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, email_contact: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="api@mtn.ci"
                        />
                        <p className="mt-1 text-xs text-gray-500">L'adresse à laquelle l'ordre de virement sera envoyé.</p>
                      </div>
                      <div className="flex items-center mt-4">
                        <input
                          id="est_actif"
                          type="checkbox"
                          checked={currentCompte?.est_actif || false}
                          onChange={(e) => setCurrentCompte({ ...currentCompte, est_actif: e.target.checked })}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="est_actif" className="ml-2 block text-sm text-gray-900">
                          Compte actif
                        </label>
                      </div>
                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                        <button
                          type="submit"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm"
                        >
                          Enregistrer
                        </button>
                        <button
                          type="button"
                          onClick={handleCloseModal}
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                        >
                          Annuler
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
